import type { InvoiceDto, PosSeller, PosStateDto, ShiftReportDto } from "@delta/shared";
import { amt } from "../pages/ledger/common";
import type { Block, PrintDoc, PrintImage } from "./render";
import { carriesQr, invoiceQr, qrProblemText } from "./qr";
import { partyName } from "../lib/party";

// The documents Delta prints directly (phase 12a). Receipts are NON-FISCAL while ZATCA phase 2 is deferred
// (docs/non-fiscal-pos.md): they say so at the top and again in a box, carry no tax-invoice title, and name the
// compliant system that issues the legal tax invoice, with its number when one was recorded. A posted receipt carries
// the phase 1 QR (print/qr.tsx; not phase 2), or a box saying the seller's tax details are missing or invalid.
// A copy is marked COPY with its number; a server that is not production marks every receipt TEST DOCUMENT.

type T = (k: string, v?: Record<string, string | number>) => string;
type L = (x: { nameEn: string; nameAr: string }) => string;
export interface DocText { t: T; L: L; lang: "en" | "ar" }

const qtyText = (q: string) => q.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");

export interface ReceiptOptions {
  copyNo: number;
  buyerTaxNumber?: string;
  /** The legal tax invoice number from the compliant system, when recorded. */
  legalReference?: string | null;
  /** A4 prints the same receipt larger. */
  large?: boolean;
  /** The server is not production: the receipt says TEST DOCUMENT at its top and its end. */
  testDocument?: boolean;
  /** The organization logo, loaded (loadPrintImage); without one the organization's name heads the receipt alone. */
  logo?: PrintImage | null;
}

/** Where the organization logo is read, versioned so a replaced logo is fetched again (null: no logo set). */
export const logoUrl = (seller: Pick<PosSeller, "logo">) => (seller.logo ? `/api/organization/logo?v=${encodeURIComponent(seller.logo)}` : null);

/** The non-fiscal receipt of a POS sale or refund, for a 58 mm or 80 mm roll or an A4 sheet. */
export function receiptDoc(inv: InvoiceDto, seller: PosStateDto["seller"], x: DocText, o: ReceiptOptions): PrintDoc {
  const { t, L, lang } = x, s = o.large ? 1.35 : 1, sz = (n: number) => n * s;
  const refund = inv.kind === "sale_refund";
  const sellerName = lang === "ar" ? seller.nameAr || seller.nameEn : seller.nameEn || seller.nameAr;
  const system = seller.legalInvoiceSystem;
  const b: Block[] = [];
  if (o.testDocument) b.push({ t: "box", text: t("printTestBanner"), bold: true, size: sz(8.5) }, { t: "space", mm: 1.5 });
  if (o.copyNo > 0) b.push({ t: "banner", text: t("printCopyBanner", { n: o.copyNo }), size: sz(11) }, { t: "space", mm: 1.5 });
  b.push({ t: "banner", text: refund ? t("nonFiscalRefundTitle") : t("nonFiscalTitle"), size: sz(10) }, { t: "space", mm: 2 });
  if (o.logo) b.push({ t: "image", img: o.logo, mm: o.large ? 22 : 16, widthMm: o.large ? 60 : 44 }, { t: "space", mm: 1.5 });
  b.push({ t: "text", text: sellerName, size: sz(11), bold: true, align: "center" });
  if (seller.address) b.push({ t: "text", text: seller.address, align: "center", size: sz(8) });
  b.push({ t: "text", text: `${L(inv.branch)} · ${L(inv.warehouse)}`, align: "center", size: sz(8) });
  if (seller.taxNumber) b.push({ t: "text", text: `${t("vatNo")}: ${seller.taxNumber}`, align: "center", size: sz(8) });
  b.push({ t: "rule" });
  b.push({ t: "kv", k: t("receiptNo"), v: inv.code ?? "", bold: true, size: sz(9) });
  if (refund && inv.refundOf?.code) b.push({ t: "kv", k: t("refundOfLabel"), v: inv.refundOf.code, size: sz(8.5) });
  b.push({ t: "kv", k: t("date"), v: inv.invoiceAt.replace("T", " "), size: sz(8.5) });
  b.push({ t: "kv", k: t("customerName"), v: partyName(t, inv.party), size: sz(8.5) });
  if (o.buyerTaxNumber) b.push({ t: "kv", k: t("vatNo"), v: o.buyerTaxNumber, size: sz(8.5) });
  b.push({ t: "kv", k: t("cashier"), v: inv.postedByName ?? inv.createdByName ?? "", size: sz(8.5) });
  if (inv.shift) b.push({ t: "kv", k: t("shiftNo"), v: inv.shift.number, size: sz(8.5) });
  b.push({ t: "rule", dashed: true });
  b.push({ t: "row", bold: true, size: sz(8), cells: [{ text: t("items"), w: 5 }, { text: t("qtyShort"), w: 1.4, align: "end" }, { text: t("price"), w: 2, align: "end" }, { text: t("total"), w: 2.2, align: "end" }] });
  for (const l of inv.lines) {
    b.push({ t: "row", size: sz(8), cells: [{ text: `${L(l.item)} (${L(l.unit)})`, w: 5 }, { text: qtyText(l.qty), w: 1.4, align: "end", dir: "ltr" }, { text: amt(l.price), w: 2, align: "end", dir: "ltr" }, { text: amt(l.total), w: 2.2, align: "end", dir: "ltr" }] });
  }
  b.push({ t: "rule", dashed: true });
  if (!refund) {
    b.push({ t: "kv", k: t("totalBeforeDiscount"), v: amt(inv.totalBefore), size: sz(8.5) });
    if (/[1-9]/.test(inv.discount)) b.push({ t: "kv", k: t("discount"), v: `-${amt(inv.discount)}`, size: sz(8.5) });
  }
  b.push({ t: "kv", k: t("subTotal"), v: amt(inv.subtotal), size: sz(8.5) });
  if (/[1-9]/.test(inv.tobaccoFee ?? "0")) b.push({ t: "kv", k: t("tobFee"), v: amt(inv.tobaccoFee), size: sz(8.5) });
  b.push({ t: "kv", k: t("vatIncluded"), v: amt(inv.vat), size: sz(8.5) });
  b.push({ t: "kv", k: refund ? t("refundTotal") : t("grandTotal"), v: `${amt(inv.grand)} ${t("currency")}`, bold: true, size: sz(10.5) });
  if (!refund && /[1-9]/.test(inv.tobacco?.raised ?? "0")) b.push({ t: "text", text: t("tobRaisedNote", { x: amt(inv.tobacco!.raised) }), size: sz(7.5) });
  for (const p of inv.payments) b.push({ t: "kv", k: L(p.treasury), v: amt(p.amount), size: sz(8.5) });
  if (!refund && !/^0(\.0+)?$/.test(inv.remaining) && !inv.remaining.startsWith("-")) b.push({ t: "kv", k: t("deferred"), v: amt(inv.remaining), size: sz(8.5) });
  if (inv.cashTendered) b.push({ t: "kv", k: t("cashReceived"), v: amt(inv.cashTendered), size: sz(8.5) }, { t: "kv", k: t("changeDue"), v: amt(inv.cashChange ?? "0.00"), size: sz(8.5) });
  b.push({ t: "space", mm: 2 });
  b.push({ t: "box", text: system ? t("nonFiscalNoticeSystem", { system }) : t("nonFiscalNotice"), bold: true, size: sz(8.5) });
  if (o.legalReference) b.push({ t: "kv", k: t("legalInvoiceNo"), v: o.legalReference, bold: true, size: sz(8.5) });
  if (carriesQr(inv)) {
    const q = invoiceQr(seller, inv);
    b.push({ t: "space", mm: 2 });
    if (q.ok) b.push({ t: "qr", text: q.text, mm: o.large ? 32 : 28 }, { t: "text", text: t("qrPhase1Note"), align: "center", size: sz(7) });
    else b.push({ t: "box", text: `${t("qrMissingTitle")}: ${qrProblemText(t, q.problems).join("; ")}. ${t("qrMissingHint")}`, bold: true, size: sz(8) });
  }
  if (inv.code) b.push({ t: "space", mm: 2 }, { t: "barcode", text: inv.code, mm: 9, widthMm: 50 }, { t: "text", text: inv.code, align: "center", size: sz(8), dir: "ltr" });
  b.push({ t: "space", mm: 1.5 }, { t: "text", text: t("thankYou"), align: "center", size: sz(8.5) });
  if (o.copyNo > 0) b.push({ t: "space", mm: 1.5 }, { t: "banner", text: t("printCopyBanner", { n: o.copyNo }), size: sz(9) });
  if (o.testDocument) b.push({ t: "space", mm: 1.5 }, { t: "box", text: t("printTestBanner"), bold: true, size: sz(8.5) });
  b.push({ t: "space", mm: 4 });
  return { dir: lang === "ar" ? "rtl" : "ltr", blocks: b };
}

/** A test page: both languages, digits, a QR code, a barcode and a width ruler, to check a printer and its paper. */
export function testPageDoc(x: DocText, o: { printer: string; role: string; paper: string; at: string }): PrintDoc {
  const { t, lang } = x;
  return {
    dir: lang === "ar" ? "rtl" : "ltr",
    blocks: [
      { t: "banner", text: t("printTestTitle") },
      { t: "space", mm: 2 },
      { t: "kv", k: t("printerName"), v: o.printer },
      { t: "kv", k: t("printerRole"), v: o.role },
      { t: "kv", k: t("paper"), v: o.paper },
      { t: "kv", k: t("date"), v: o.at },
      { t: "rule" },
      { t: "text", text: "فاتورة المبيعات رقم ١٢٣ – Sales receipt No. 123", align: "center", bold: true, dir: "rtl" },
      { t: "text", text: "Delta دلتا · 0123456789 · ٠١٢٣٤٥٦٧٨٩", align: "center", dir: "ltr" },
      { t: "text", text: "العربية من اليمين إلى اليسار، English left to right.", align: "start" },
      { t: "row", cells: [{ text: "الصنف / Item", w: 5 }, { text: "1.5", w: 1.5, align: "end", dir: "ltr" }, { text: "12.75", w: 2, align: "end", dir: "ltr" }] },
      { t: "rule", dashed: true },
      { t: "qr", text: "DELTA-TEST-QR", mm: 22 },
      { t: "barcode", text: "DELTA-123", mm: 9, widthMm: 45 },
      { t: "text", text: "DELTA-123", align: "center", dir: "ltr" },
      { t: "rule" },
      { t: "text", text: t("printTestNote"), align: "center", size: 7.5 },
      { t: "space", mm: 4 }
    ]
  };
}

export interface LabelData { org: string; nameAr: string; nameEn: string; unitAr: string; unitEn: string; barcode: string; price: string }

/** One label: organization, item names, unit, Code 128 barcode, the code and the retail price. */
export function labelDoc(l: LabelData, x: DocText): PrintDoc {
  const name = l.nameEn === l.nameAr ? l.nameEn : `${l.nameAr} · ${l.nameEn}`;
  return {
    dir: x.lang === "ar" ? "rtl" : "ltr",
    blocks: [
      { t: "text", text: l.org, size: 6.5, align: "center" },
      { t: "text", text: name, size: 7.5, bold: true, align: "center" },
      { t: "text", text: `${l.unitAr} · ${l.unitEn}`, size: 6.5, align: "center" },
      { t: "barcode", text: l.barcode, mm: 7, widthMm: 40 },
      { t: "text", text: l.barcode, size: 6.5, align: "center", dir: "ltr" },
      { t: "text", text: `${amt(l.price)} ${x.t("currency")}`, size: 8, bold: true, align: "center" }
    ]
  };
}

/**
 * The shift report (POS): the same figures as the screen, for an 80 mm or 58 mm roll or an A4 page (large). Every
 * amount comes from the server's report (stored transactions, checked against the ledger); nothing is worked out here.
 */
export function shiftReportDoc(r: ShiftReportDto, x: DocText, o: { large?: boolean; logo?: PrintImage | null }): PrintDoc {
  const { t, L, lang } = x, s = o.large ? 1.1 : 1, sz = (n: number) => n * s, sh = r.shift;
  const sellerName = lang === "ar" ? r.seller.nameAr || r.seller.nameEn : r.seller.nameEn || r.seller.nameAr;
  const kv = (k: string, v: string, bold = false) => b.push({ t: "kv", k, v, bold, size: sz(bold ? 9 : 8.5) });
  // A heading stays on the page of its first row (render.ts, keep).
  const head = (text: string) => b.push({ t: "space", mm: 1.5, keep: true }, { t: "text", text, bold: true, size: sz(9.5), keep: true }, { t: "rule", dashed: true, keep: true });
  const time = (iso: string) => iso.replace("T", " ").slice(0, 16);
  const b: Block[] = [];
  if (r.testDocument) b.push({ t: "box", text: t("printTestBanner"), bold: true, size: sz(8.5) }, { t: "space", mm: 1.5 });
  if (o.logo) b.push({ t: "image", img: o.logo, mm: o.large ? 20 : 14, widthMm: o.large ? 60 : 40 }, { t: "space", mm: 1.5 });
  b.push({ t: "text", text: sellerName, size: sz(10.5), bold: true, align: "center" });
  if (r.seller.taxNumber) b.push({ t: "text", text: `${t("vatNo")}: ${r.seller.taxNumber}`, align: "center", size: sz(8), dir: lang === "ar" ? "rtl" : "ltr" });
  b.push({ t: "space", mm: 1.5 }, { t: "banner", text: `${t("srTitle")} · ${t("srShift", { n: sh.number })}`, size: sz(10.5) }, { t: "space", mm: 1.5 });
  kv(t("cashier"), sh.user.name);
  kv(t("branch"), L(sh.branch));
  kv(t("cashTreasuryLabel"), L(sh.treasury));
  kv(t("workStarted"), time(new Date(Date.parse(sh.openedAt) + 3 * 3600_000).toISOString()));
  if (sh.closedAt) { kv(t("srClosedAt"), time(new Date(Date.parse(sh.closedAt) + 3 * 3600_000).toISOString())); kv(t("srClosedBy"), sh.closedByName ?? ""); }
  else kv(t("status"), t("srOpen"));
  kv(t("srGenerated"), time(r.generatedAt));

  head(t("srSales"));
  kv(t("srSalesCount"), `${r.sales.count} (${t("srWalkIn")} ${r.sales.walkIn})`);
  if (/[1-9]/.test(r.sales.discount)) kv(t("srDiscount"), `-${amt(r.sales.discount)}`);
  if (/[1-9]/.test(r.sales.tobaccoFee ?? "0")) kv(t("tobFee"), amt(r.sales.tobaccoFee));
  kv(t("srVat"), amt(r.sales.vat));
  kv(t("srSalesTotal"), amt(r.sales.total), true);
  kv(t("srRefundsCount"), String(r.refunds.count));
  if (/[1-9]/.test(r.refunds.tobaccoFee ?? "0")) kv(t("tobFee"), amt(r.refunds.tobaccoFee));
  kv(t("srVat"), amt(r.refunds.vat));
  kv(t("srRefundsTotal"), amt(r.refunds.total), true);
  kv(t("srNet"), `${amt(r.net)} ${t("currency")}`, true);
  kv(t("srOnAccount"), amt(r.onAccount));
  kv(t("srTendered"), amt(r.tendered));
  kv(t("srChange"), amt(r.change));

  head(t("srMethods"));
  const ledger = (m: ShiftReportDto["methods"][number]): Block => ({ t: "text", text: `${t("srLedger")} ${amt(m.ledger)}: ${m.matches ? t("srLedgerOk") : t("srLedgerBad", { x: amt(m.ledger) })}`, size: sz(7), bold: !m.matches });
  if (o.large) {
    // A4: one table, its heading row kept with the first method.
    b.push({ t: "row", bold: true, size: sz(7.5), keep: true, cells: [{ text: t("srMethod"), w: 3.2 }, { text: t("srMethodSales"), w: 2, align: "end" }, { text: t("srCommission"), w: 1.8, align: "end" }, { text: t("srMethodRefunds"), w: 1.8, align: "end" }, { text: t("srMethodNet"), w: 2, align: "end" }] });
    for (const m of r.methods) {
      b.push({ t: "row", size: sz(7.5), keep: true, cells: [{ text: L(m.treasury), w: 3.2 }, { text: amt(m.sales), w: 2, align: "end", dir: "ltr" }, { text: amt(m.commission), w: 1.8, align: "end", dir: "ltr" }, { text: amt(m.refunds), w: 1.8, align: "end", dir: "ltr" }, { text: amt(m.net), w: 2, align: "end", dir: "ltr" }] });
      b.push(ledger(m));
    }
  } else {
    // A roll is too narrow for five columns (the headings broke inside words on 58 mm): each method is a small block of
    // label and amount lines.
    for (const m of r.methods) {
      b.push({ t: "text", text: L(m.treasury), bold: true, size: sz(8.5), keep: true });
      for (const [k, v] of [[t("srMethodSales"), m.sales], [t("srCommission"), m.commission], [t("srMethodRefunds"), m.refunds]] as const) b.push({ t: "kv", k, v: amt(v), size: sz(8), keep: true });
      b.push({ t: "kv", k: t("srMethodNet"), v: amt(m.net), bold: true, size: sz(8), keep: true }, ledger(m), { t: "space", mm: 1 });
    }
  }
  if (!r.methods.length) b.push({ t: "text", text: t("srNoInvoices"), size: sz(8) });

  head(t("srCash"));
  kv(t("openingFloat"), amt(r.cash.openingFloat));
  kv(t("srCashSales"), amt(r.cash.sales));
  kv(t("srCashRefunds"), amt(r.cash.refunds));
  kv(t("srInflows"), amt(r.cash.inflows));
  kv(t("srOutflows"), amt(r.cash.outflows));
  kv(t("srExpected"), amt(r.cash.expected), true);
  if (r.cash.counted === null) kv(t("srCounted"), t("srNotCounted"));
  else { kv(t("srCounted"), amt(r.cash.counted), true); kv(t("srDifference"), amt(r.cash.difference ?? "0"), true); }
  if (sh.note) kv(t("note"), sh.note);
  if (!r.drawer.complete) b.push({ t: "box", text: t("srIncomplete", { n: r.drawer.unresolved.count, x: amt(r.drawer.unresolved.amount), m: r.drawer.unattributed.count, y: amt(r.drawer.unattributed.amount) }), size: sz(7.5), bold: true });

  head(t("srDrawer"));
  for (const m of r.drawer.movements) {
    b.push({ t: "row", size: sz(7.5), keep: !!m.reason, cells: [{ text: m.code ?? "–", w: 2.4, dir: "ltr" }, { text: `${t(`srMv_${m.kind}`)}${m.event === "reversal" ? ` · ${t("srEvent_reversal")}` : ""}`, w: 3.8 }, { text: `${m.direction === "out" ? "-" : ""}${amt(m.amount)}`, w: 2.2, align: "end", dir: "ltr" }] });
    if (m.reason) b.push({ t: "text", text: t(`srReason_${m.reason}`), size: sz(6.5), bold: true });
  }
  if (!r.drawer.movements.length) b.push({ t: "text", text: t("srDrawerNone"), size: sz(8) });
  if (r.drawer.unattributed.count) b.push({ t: "text", text: t("srUnattributed", { n: r.drawer.unattributed.count, x: amt(r.drawer.unattributed.amount) }), size: sz(7), bold: true });

  head(t("srInvoices"));
  for (const i of r.invoices) {
    const pays = i.payments.map((p) => `${L(p.treasury)} ${amt(p.amount)}`).join(" + ");
    // On a roll the time moves to the detail line: a time column there was too narrow and broke.
    const at = time(i.invoiceAt).slice(11);
    b.push({ t: "row", size: sz(7.5), keep: !!pays || !o.large, cells: [{ text: `${i.code}${i.reversed ? ` (${t("srReversed")})` : ""}`, w: 2.4, dir: "ltr" }, ...(o.large ? [{ text: at, w: 1.3, dir: "ltr" as const }] : []), { text: partyName(t, i.party), w: 3.5 }, { text: `${i.kind === "sale_refund" ? "-" : ""}${amt(i.grand)}`, w: 2.2, align: "end", dir: "ltr" }] });
    const detail = [...(o.large ? [] : [at]), ...(pays ? [pays] : []), ...(i.tendered ? [`${t("srTendered")} ${amt(i.tendered)}`, `${t("srChange")} ${amt(i.change ?? "0")}`] : [])].join(" · ");
    if (detail) b.push({ t: "text", text: detail, size: sz(6.5) });
  }
  if (!r.invoices.length) b.push({ t: "text", text: t("srNoInvoices"), size: sz(8) });
  // A short footer, kept with the last row so it never sits alone on a page. The staging mark is printed once, at the
  // top; the rules behind the figures are explained on the screen, not on paper.
  b.push({ t: "rule", withPrev: true }, { t: "text", text: t("srPrintFooter"), size: sz(7), align: "center", withPrev: true }, { t: "space", mm: 3, withPrev: true });
  return { dir: lang === "ar" ? "rtl" : "ltr", blocks: b };
}
